import { useMemo, useState, Fragment } from "react";
import { Button } from "reactstrap";
import { sortBy } from "underscore";

import CreateFieldDrawer from "../../components/Drawers/Fields/CreateFieldDrawer";
import useCreateField from "../../api/api-hooks/modules/fields/useCreateField";
import useModifyField from "../../api/api-hooks/modules/fields/useModifyField";
import useDeleteField from "../../api/api-hooks/modules/fields/useDeleteField";
import EditFieldDrawer from "../../components/Drawers/Fields/EditFieldDrawer";
import AppPageHeader from "../../components/Headers/AppPageHeader";
import SkeletonLoader from "../../elements/SkeletonLoader";
import { AppConfirm } from "../../utils/PopupUtil";
import useToggle from "../../hooks/useToggle";
import useModule from "../../hooks/useModule";
import ItemCardList from "./ItemCardList";
import SummaryCard from "./SummaryCard";

const ManageModuleFields = ({ moduleAPIName }: { moduleAPIName: string }) => {
  const [createFieldDrawer, toggleCreateFieldDrawer] = useToggle();
  const [editField, setEditField] = useState<any>(null);
  const { createField, creatingField, createFieldError } = useCreateField();
  const { modifyField, modifyingField } = useModifyField();
  const { deleteField, deleteFieldID, deleteFieldError } = useDeleteField();
  const { module: currentModule, modulesLoading } = useModule(
    moduleAPIName,
    false,
    true
  );
  const [modifyError, setModifyError] = useState("");

  const handleModifyField = (
    fieldAPIName: string,
    fieldValues: any,
    onSuccess: () => void
  ) => {
    if (!currentModule) return;
    modifyField({
      moduleAPIName: currentModule.APIName,
      fieldAPIName: fieldAPIName,
      fieldValues: fieldValues,
      supportTargetCloudID: undefined, // This is NOT a support request.
      onSuccess: onSuccess,
      onError: (error) => {
        setModifyError(
          error || deleteFieldError || "Failed to modify the field."
        );
      },
    });
  };

  const handleCreateField = (
    fieldValues: Record<string, any>,
    onSuccess: () => void,
    onError: () => void
  ) => {
    if (!currentModule) return;

    createField({
      moduleAPIName: currentModule.APIName,
      fieldValues: fieldValues,
      onSuccess: onSuccess,
      onError: onError,
      supportTargetCloudID: undefined, // This is NOT a support request.
    });
  };

  const startFieldDelete = async (field: Record<string, any>) => {
    if (!currentModule) return;

    if (currentModule?.CalculationFields?.hasOwnProperty(field.APIName)) {
      return await AppConfirm({
        title: `You can't delete '${field.Name}'`,
        html: `The field ${
          field.Name
        } is being used on the following Calculation Fields: ${currentModule.CalculationFields[
          field.APIName
        ].map((item) => item)}`,
        showConfirmButton: false,
        showCancelButton: false,
      });
    }

    const result = await AppConfirm({
      title: `Delete '${field.Name}' Field?`,
      html: `Are you sure you want to delete the '${field.Name}' field? <br/> This may cause inconsistencies with your records and any references associated with this module.`,
    });
    if (!result.isConfirmed) return;

    deleteField({
      moduleAPIName: currentModule.APIName,
      fieldAPIName: field.APIName,
      supportTargetCloudID: undefined, // This is NOT a support request.
    });
  };

  const customFields = useMemo(() => {
    if (!currentModule) return;
    return sortBy(
      currentModule?.Fields.filter((f) => !f.SystemField),
      "Name"
    );
  }, [currentModule]);

  const defaultFields = useMemo(() => {
    if (!currentModule) return;
    return sortBy(
      currentModule?.Fields.filter((f) => f.SystemField === true),
      "Name"
    );
  }, [currentModule]);

  if (modulesLoading) return <SkeletonLoader height="50vh" />;

  if (!currentModule) return null;

  return (
    <>
      <div>
        <div className="pt-3 pb-3 mb-3 border-bottom border-lighter">
          <AppPageHeader
            size="sm"
            title={"Custom Fields"}
            description={`Create and manage the custom fields for this data source.`}
            customContent={
              <>
                <Button
                  size="sm"
                  color="primary"
                  onClick={toggleCreateFieldDrawer}
                  style={{ width: 80 }}
                >
                  Add
                </Button>
              </>
            }
          />
        </div>
        {!customFields ? (
          <div>Loading...</div>
        ) : (
          <>
            {customFields?.map((f, i) => (
              <Fragment key={f.APIName}>
                <ItemCardList
                  removable
                  removeAction={() => startFieldDelete(f)}
                  removeStatus={deleteFieldID}
                  editable
                  editableAction={setEditField}
                  summary
                  item={f}
                >
                  <SummaryCard field={f} />
                </ItemCardList>
                {currentModule?.Fields?.length - 1 !== i && (
                  <>
                    <hr className="my-0"></hr>
                  </>
                )}
              </Fragment>
            ))}
            {customFields?.length === 0 && (
              <div
                className="border bg-ultralight rounded p-2 mb--3 font-weight-500"
                style={{ width: "fit-content" }}
              >
                No custom fields have been added.
              </div>
            )}
          </>
        )}
      </div>
      <div className="mb-5">
        <div className="pt-5 pb-3 border-bottom border-lighter mb-3">
          <AppPageHeader
            size="sm"
            title={"Default Fields"}
            description={`Default fields are automatically included with this data source.`}
          />
        </div>
        {defaultFields?.length === 0 ? (
          <div>Loading...</div>
        ) : (
          <>
            {defaultFields?.map((f, i) => (
              <Fragment key={f.APIName}>
                <ItemCardList summary item={f}>
                  <SummaryCard field={f} />
                </ItemCardList>
                {currentModule?.Fields?.length - 1 !== i && (
                  <>
                    <hr className="my-0"></hr>
                  </>
                )}
              </Fragment>
            ))}
          </>
        )}
      </div>
      <EditFieldDrawer
        open={editField !== null}
        field={editField}
        submitting={modifyingField}
        onClose={() => {
          setEditField(null);
          setModifyError("");
        }}
        onSave={handleModifyField}
        error={modifyError}
        moduleAPIName={moduleAPIName}
        availableFieldTypes={undefined}
      />
      <CreateFieldDrawer
        open={createFieldDrawer}
        onClose={toggleCreateFieldDrawer}
        submitting={creatingField}
        onSave={handleCreateField}
        error={createFieldError}
        moduleAPIName={moduleAPIName}
      />
    </>
  );
};

export default ManageModuleFields;
